import { List, ListItem, ListItemIcon, ListItemText } from "@mui/material";

import { CheckIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { IZoneUserRole, ZoneUserRole } from "constants/userContstants";

const userRoles = [
  {
    value: "manage_zones",
    permissions: [ZoneUserRole.OWNER] as IZoneUserRole[],
  },
  {
    value: "manage_users_and_assign_roles_to_zones",
    permissions: [ZoneUserRole.OWNER, ZoneUserRole.ADMIN] as IZoneUserRole[],
  },
  {
    value: "manage_somnofy_devices",
    permissions: [ZoneUserRole.OWNER, ZoneUserRole.ADMIN] as IZoneUserRole[],
  },
  {
    value: "manage_subjects",
    permissions: [
      ZoneUserRole.OWNER,
      ZoneUserRole.ADMIN,
      ZoneUserRole.MANAGER,
    ] as IZoneUserRole[],
  },

  {
    value: "assign_somnofy_device_to_subject",
    permissions: [
      ZoneUserRole.OWNER,
      ZoneUserRole.ADMIN,
      ZoneUserRole.MANAGER,
    ] as IZoneUserRole[],
  },
  {
    value: "change_somnofy_device_settings",
    permissions: [
      ZoneUserRole.OWNER,
      ZoneUserRole.ADMIN,
      ZoneUserRole.MANAGER,
      ZoneUserRole.MANAGER_LIMITED,
    ] as IZoneUserRole[],
  },
  {
    value: "manage_notifications",
    permissions: [
      ZoneUserRole.OWNER,
      ZoneUserRole.ADMIN,
      ZoneUserRole.MANAGER,
    ] as IZoneUserRole[],
  },
  {
    value: "view_live_dashboard",
    permissions: [
      ZoneUserRole.DATA_READER,
      ZoneUserRole.DATA_READER_LIMITED,
      ZoneUserRole.SERVICE_MONITOR,
    ] as IZoneUserRole[],
  },
  {
    value: "view_night_report",
    permissions: [
      ZoneUserRole.DATA_READER,
      ZoneUserRole.DATA_READER_LIMITED,
    ] as IZoneUserRole[],
  },
  {
    value: "view_sleep_history",
    permissions: [ZoneUserRole.DATA_READER] as IZoneUserRole[],
  },
  {
    value: "stay_logged_in",
    permissions: [ZoneUserRole.SERVICE_MONITOR] as IZoneUserRole[],
  },
] as const;

const isActive = (roles: IZoneUserRole[], permissions: IZoneUserRole[]) =>
  roles.some((r) => permissions.includes(r));

type RoleInformationProps = {
  roles: IZoneUserRole[];
};

export function RoleInformation({ roles }: RoleInformationProps) {
  const { t } = useTranslation();

  return (
    <List disablePadding>
      {userRoles.map((userRole) => {
        const active = isActive(roles, userRole.permissions);

        return (
          <ListItem disablePadding key={userRole.value}>
            <ListItemIcon
              sx={(theme) => ({
                minWidth: 0,
                paddingRight: 1,
                color: active
                  ? theme.vars.palette.backgrounds.success.strong.default
                  : theme.vars.palette.backgrounds.danger.strong.default,
              })}
            >
              {active ? <CheckIcon /> : <XIcon />}
            </ListItemIcon>
            <ListItemText
              primary={t(userRole.value)}
              slotProps={{
                primary: {
                  variant: "bodySmallRegular",
                  color: active ? "textPrimary" : "textSecondary",
                },
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );
}
